import { useCallback, useEffect, useState } from 'react';
import {
  createLanguageRequest,
  deleteLanguageRequest,
  listLanguagesRequest,
  updateLanguageRequest
} from '@services/languages/languagesApi';

// Normalizes known API response variants into a single array shape.
function extractLanguages(body) {
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.languages)) return body.languages;
  if (body?.language) return [body.language];
  return [];
}

// CRUD hook for language catalog management screens.
function useLanguages() {
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchLanguages = useCallback(async () => {
    // Initial and post-mutation refresh entrypoint.
    setError('');
    try {
      const response = await listLanguagesRequest();
      setLanguages(extractLanguages(response.body));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLanguages();
  }, [fetchLanguages]);

  async function createLanguage(payload) {
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const response = await createLanguageRequest(payload);
      setSuccess(response.message || 'Language created.');
      await fetchLanguages();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function updateLanguage(id, payload) {
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const response = await updateLanguageRequest(id, payload);
      setSuccess(response.message || 'Language updated.');
      await fetchLanguages();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function removeLanguage(id) {
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const response = await deleteLanguageRequest(id);
      setSuccess(response.message || 'Language deleted.');
      await fetchLanguages();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return {
    languages,
    loading,
    submitting,
    error,
    success,
    refreshLanguages: fetchLanguages,
    createLanguage,
    updateLanguage,
    removeLanguage
  };
}

export default useLanguages;
